/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { isEmpty, map, filter, find, get, set, cloneDeep } from 'lodash';
import { Modal, Table } from '../../../../components';
import { FormattedMessage } from 'react-intl';
import type { Appointment as AppointmentType } from '../../../../type';

type AppointmentRowProps = {
    appointment: AppointmentType,
};

const GET_APPOINTMENTS_QUERY = gql`
    query getMasterAppointments($master: ID!, $salon: ID!, $lang: String!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    master(id: $master) {
                        id
                        appointments {
                            id
                            startAt
                            service {
                                id
                                name(lang: $lang)
                                price
                            }
                            invoice {
                                id
                                paid
                                items {
                                    id
                                    type
                                    quantity
                                    price
                                    entity {
                                        ... on Service {
                                            id
                                            name(lang: $lang)
                                        }
                                        ... on Product {
                                            id
                                            name
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

// Fix styling of spinner inside modal window
const Spinner = styled(Loader)`
    &::before {
        border: 0.2em solid rgba(0, 0, 0, 0.1) !important;
    }
    &::after {
        border-color: #767676 transparent transparent !important;
    }
`;

/**
 * Show this message if list of appointments is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={3} textAlign="center">
            <FormattedMessage
                id="pages.staff.details.appointments.noappointments"
                defaultMessage="This master don't have appointments"
            />
        </Table.Cell>
    </Table.Row>
);

/**
 * A row of single appointment
 */
const AppointmentRow = ({ appointment }: AppointmentRowProps) => (
    <Table.Row>
        <Table.Cell>
            {format(new Date(appointment.startAt), 'dd.MM.yyyy HH:mm')}
        </Table.Cell>
        <Table.Cell>{appointment.service.name}</Table.Cell>
        <Table.Cell textAlign="right">
            {/* different data, string and integer can come here, so you need to convert them,
        this is a temporary fix until the problem with data types on the server is fixed */}
            {parseFloat(appointment.service.price).toFixed(2)} €
        </Table.Cell>
    </Table.Row>
);

function Appointments({ master }: { master: { id: string } }) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const currentLanguage = useSelector(
        ({ intl }) => intl.get('locale') || intl.get('defaultLanguage'),
    );

    const { data, loading } = useQuery(GET_APPOINTMENTS_QUERY, {
        variables: { salon, master: master.id, lang: currentLanguage },
    });

    if (loading) {
        return (
            <Modal.Context>
                <Modal.Content>
                    <Spinner size="big" inline="centered" active />
                </Modal.Content>
            </Modal.Context>
        );
    }

    const rawAppointments = data?.viewer?.salon?.master?.appointments ?? null;
    const appointments =
        !loading && rawAppointments
            ? map(
                  filter(rawAppointments, appt => get(appt, 'invoice.paid')),
                  appt => {
                      appt = cloneDeep(appt);
                      const invoiceItem = find(
                          get(appt, 'invoice.items'),
                          item =>
                              get(item, 'entity.id') ===
                              get(appt, 'service.id'),
                      );
                      if (invoiceItem) set(appt, 'service.price', invoiceItem.price);
                      return appt;
                  },
              )
            : null;

    return (
        <Modal.Context>
            <Modal.Content>
                <Table celled striped selectable>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <FormattedMessage
                                    id="pages.staff.details.appointments.date"
                                    defaultMessage="Date"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>
                                <FormattedMessage
                                    id="pages.staff.details.appointments.service"
                                    defaultMessage="Service"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell textAlign="right">
                                <FormattedMessage
                                    id="pages.staff.details.appointments.price"
                                    defaultMessage="Price"
                                />
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {isEmpty(appointments) ? (
                            <EmptyMessage />
                        ) : (
                            map(appointments, appointment => (
                                <AppointmentRow
                                    key={appointment.id}
                                    appointment={appointment}
                                />
                            ))
                        )}
                    </Table.Body>
                </Table>
            </Modal.Content>
        </Modal.Context>
    );
}

export default Appointments;
